<script setup lang="ts">
import store from "@/store";
import {
  weightRegex,
  heightCodeRegex,
  loadingUnitRegex,
  licensePlateRegex,
  containerTypeRegex,
  CARRIER_BOOKED_ORDER,
  LOADED_AT_SENDER,
  DIRECTION_DELIVERY,
  COUNTER_CANCELLATION_NEEDED,
} from "@/utils/constants";
import { populateParams } from "@/utils/methods";

import DbInput from "@/components/DB-Components/DbInput.vue";
import OrderContainerTypeInput from "./OrderContainerTypeInput.vue";
import OrderHeightInput from "./OrderHeightInput.vue";
import OrderWeightInput from "./OrderWeightInput.vue";
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import { DbButton } from "@db-ui/v-elements-enterprise/dist/components";
import StatusChangeButton from "@/components/StatusChangeButton.vue";
import DiscardOrderButton from "@/components/DiscardOrderButton.vue";
import { i18n } from "@/i18n/i18n";
import _ from "lodash";
const { t } = i18n.global;
const emit = defineEmits(["close", "refreshData"]);

interface FormValidation {
  loading_unit_formatted: string;
  type: string;
  heightCode: string;
  loadWeight: string;
  emptyWeight: string;
  licensePlate: string;
}

const props = defineProps({
  order: {
    type: Object,
    default: () => ({
      orderId: null,
      orderPosition: null,
      primaryReference: null,
      secondaryReference: null,
      directionType: null,
      transportationStatus: null,
      loading_unit_formatted: null,
      loadUnit: {
        identifierPrefix: null,
        identifierNumber: null,
        type: null,
        lengthCode: null,
        heightCode: null,
        emptyWeight: null,
        loadWeight: null,
        totalWeight: null,
        loadingStatus: null,
        dangerousGoods: false, // Set default value for boolean properties
        waste: false,
        limitedQuantity: false,
        customsGoods: false,
      },
      train: {
        trainNumber: null,
        operatorId: null,
        operatorName: null,
        shippingDate: null,
        trainClosingTime: null,
        trainArrivalTime: null,
        trainState: null,
      },
      terminal: {
        terminalId: {
          locationCode: null,
          authority: null,
        },
        terminalName: null,
      },
      freightForwarderId: null,
      freightForwarderName: null,
      licensePlate: null,
      driverId: null,
    }),
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  index: Number,
});

const originalItem = ref(_.cloneDeep(props.order));
const orderItem = ref(_.cloneDeep(props.order));
const loadUnit = ref(props.order.loadUnit);

const params = computed(() => {
  return populateParams(orderItem.value, checkIfDisabled());
});

let formValidations = reactive({
  loading_unit_formatted: { invalid: "false", rx: loadingUnitRegex },
  type: { invalid: "false", rx: containerTypeRegex },
  heightCode: { invalid: "false", rx: heightCodeRegex },
  loadWeight: { invalid: "false", rx: weightRegex },
  emptyWeight: { invalid: "false", rx: weightRegex },
  licensePlate: { invalid: "false", rx: licensePlateRegex },
});

const validateInput = (
  input: string,
  rx: RegExp,
  formValidationsKey: keyof FormValidation
) => {
  if (input !== null && typeof input !== "undefined") {
    formValidations[formValidationsKey].invalid = `${!rx.test(input)}`;
  } else {
    formValidations[formValidationsKey].invalid = "true";
    if (formValidationsKey === "loading_unit_formatted") {
      // empty string is okay for these keys
      formValidations[formValidationsKey].invalid = "false";
    }
  }
  formValidations = { ...formValidations };
};

const showError = (formValidationsKey: keyof FormValidation) => {
  let errorType = "Save";
  if (formValidationsKey === "licensePlate") {
    errorType = "LicensePlate";
  }
  return formValidations[formValidationsKey].invalid === "true"
    ? t(`order.error${errorType}`)
    : undefined;
};

const allFieldsValidated = () => {
  const formValidationsSliced = Object.keys(formValidations);
  formValidationsSliced.pop(); // remove license plate from validation
  return formValidationsSliced.every(
    (key) =>
      formValidations[key as keyof typeof formValidations].invalid === "false"
  );
};

onMounted(() => {
  const formValidationKeys: (keyof FormValidation)[] = [
    "loading_unit_formatted",
    "licensePlate",
  ];
  formValidationKeys.forEach((key) =>
    validateInput(orderItem.value[key], formValidations[key].rx, key)
  );
  const nestedFormValidationKeys: (keyof FormValidation)[] = [
    "type",
    "heightCode",
    "loadWeight",
    "emptyWeight",
  ];
  nestedFormValidationKeys.forEach((key) =>
    validateInput(loadUnit.value[key], formValidations[key].rx, key)
  );
});

const save = async () => {
  await store.dispatch("toggleLoading", true);
  let params = populateParams(orderItem.value, checkIfDisabled());
  try {
    await store.dispatch("OrderStore/updateOrder", {
      order: orderItem.value,
      index: props.index,
      params: params,
    });
    emit("close");
    await store.dispatch("toggleLoading", false);
  } catch (error) {
    await store.dispatch("toggleLoading", false);
    if (error.message.includes("parallel")) {
      store.dispatch("ErrorModalStore/toggleErrorModal", {
        errorType: "parallelEditError",
      });
    }
  }
};

const close = () => {
  emit("close");
};

const licensePlateInvalid = () => {
  if (!store.state.user.data.license_plate) return true;
  return !licensePlateRegex.test(store.state.user.data.license_plate);
};

const checkIfDisabled = (input = "") => {
  if (props.readonly) return true;

  if (orderItem.value?.directionType?.toLowerCase() == DIRECTION_DELIVERY) {
    if (
      orderItem.value?.transportationStatus === CARRIER_BOOKED_ORDER ||
      orderItem.value?.transportationStatus === LOADED_AT_SENDER
    ) {
      return false;
    }
  } else {
    if (input === "licensePlate") {
      return false;
    }
  }
  return true;
};

onBeforeUnmount(() => {
  orderItem.value = originalItem.value;
});
</script>
<template>
  <div
    v-if="orderItem"
    id="order-details"
    class="bg-white min-w-full w-full z-20 overflow-auto bg-white rounded mb-5"
  >
    <div
      :class="{
        'pointer-events-none opacity-50':
          orderItem.transportationStatus === COUNTER_CANCELLATION_NEEDED,
      }"
      class="flex flex-col gap-1 p-2 mx-2 border-b"
    >
      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left min-w-[100px]"
          >{{ $t("order.loading_unit") }}:</span
        >
        <DbInput
          :disabled="checkIfDisabled()"
          v-model="orderItem.loading_unit_formatted"
          :value="orderItem.loading_unit_formatted"
          class="w-full hydrated"
          :ariainvalid="formValidations['loading_unit_formatted'].invalid"
          v-on:update:modelValue="
            () =>
              validateInput(
                orderItem.loading_unit_formatted,
                loadingUnitRegex,
                'loading_unit_formatted'
              )
          "
          :error-message="showError('loading_unit_formatted')"
        ></DbInput>
      </div>
      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left w-[100px] min-w-[100px]"
          >{{ $t("order.unit_type") }}:</span
        >
        <OrderContainerTypeInput
          :disabled="checkIfDisabled()"
          v-model="orderItem.loadUnit.type"
          :value="orderItem.loadUnit.type"
          :lengthRestriction="orderItem.loadUnit.lengthCode"
          class="w-full hydrated"
          :ariainvalid="
            formValidations['type'].invalid === 'true' ? true : false
          "
          v-on:change="
            () =>
              validateInput(orderItem.loadUnit.type, containerTypeRegex, 'type')
          "
          :error-message="showError('type')"
        >
        </OrderContainerTypeInput>
      </div>
      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left w-[100px] min-w-[100px]"
          >{{ $t("order.length") }}:</span
        >
        <DbInput
          v-model="orderItem.loadUnit.lengthCode"
          :value="orderItem.loadUnit.lengthCode"
          disabled
          class="w-full hydrated"
        ></DbInput>
      </div>
      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left w-[100px] min-w-[100px]"
          >{{ $t("order.height") }}:</span
        >
        <OrderHeightInput
          v-model="orderItem.loadUnit.heightCode"
          :disabled="checkIfDisabled()"
          :ariainvalid="
            formValidations['heightCode'].invalid === 'true' ? true : false
          "
          v-on:change="
            () =>
              validateInput(
                orderItem.loadUnit.heightCode,
                heightCodeRegex,
                'heightCode'
              )
          "
          :error-message="showError('heightCode')"
        ></OrderHeightInput>
      </div>
    </div>
    <div class="flex flex-col gap-1 p-2 mx-2 border-b">
      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left w-[140px] min-w-[140px]"
          >{{ $t("order.load_weight") }}:</span
        >
        <OrderWeightInput
          placeholder="1.000 kg"
          v-model="orderItem.loadUnit.loadWeight"
          :value="orderItem.loadUnit.loadWeight"
          :disabled="checkIfDisabled()"
          :ariainvalid="formValidations['loadWeight'].invalid"
          v-on:update:modelValue="
            () =>
              validateInput(
                `${orderItem.loadUnit.loadWeight}`,
                weightRegex,
                'loadWeight'
              )
          "
          :error-message="showError('loadWeight')"
        ></OrderWeightInput>
      </div>
      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left w-[140px] min-w-[140px]"
          >{{ $t("order.net_weight") }}:</span
        >
        <OrderWeightInput
          placeholder="4.000 kg"
          v-model="orderItem.loadUnit.emptyWeight"
          :value="orderItem.loadUnit.emptyWeight"
          :disabled="checkIfDisabled()"
          :ariainvalid="formValidations['emptyWeight'].invalid"
          v-on:update:modelValue="
            () =>
              validateInput(
                `${orderItem.loadUnit.emptyWeight}`,
                weightRegex,
                'emptyWeight'
              )
          "
          :error-message="showError('emptyWeight')"
        ></OrderWeightInput>
      </div>
      <div class="w-full flex items-center gap-2">
        <span class="text-lg text-left w-[140px] min-w-[140px]"
          >{{ $t("order.total_weight") }}:</span
        >
        <OrderWeightInput
          placeholder="4.000 kg"
          v-model="orderItem.loadUnit.totalWeight"
          :value="orderItem.loadUnit.totalWeight"
          :disabled="true"
        ></OrderWeightInput>
      </div>
    </div>
    <div v-if="licensePlateInvalid()" class="flex flex-col pt-4 pb-2">
      <p class="text-red-500 pr-2">
        {{ $t("order.add_license_plate") }}
      </p>
    </div>
    <div class="flex flex-col gap-6 p-2 mx-2">
      <div class="w-full flex justify-between items-center gap-6">
        <db-button
          @click="close"
          class="w-full hydrated"
          variant="secondary-solid"
          >{{ $t("order.cancel") }}</db-button
        >
        <db-button
          @click="save"
          class="w-full hydrated"
          variant="primary"
          :disabled="!allFieldsValidated()"
          >{{ $t("order.save") }}</db-button
        >
      </div>
      <div class="w-full flex justify-between items-center gap-6">
        <DiscardOrderButton
          :order="order"
          :index="index"
          @close="close"
          @refreshData="$emit('refreshData')"
        ></DiscardOrderButton>
        <StatusChangeButton
          v-if="orderItem"
          :order="order"
          :index="index"
          :disabled="licensePlateInvalid()"
          @close="close"
          :editedOrder="orderItem"
          :params="params"
        ></StatusChangeButton>
      </div>
    </div>
  </div>
  <div class="min-h-screen flex items-center justify-center bg-gray-100" v-else>
    <div class="text-center">
      <h1 class="text-4xl font-bold mb-4">404</h1>
      <p class="text-gray-700">{{ $t("order.pageNotFound") }}</p>
    </div>
  </div>
</template>
